'use client';

import React, { useState, useRef, useEffect } from 'react';
import { motion, useAnimation, PanInfo } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiAward } from 'react-icons/fi';

import { IconType } from 'react-icons';
import { asText, RichTextField } from '@prismicio/client';

export interface Feature {
  icon: IconType;
  image: string;
}

export interface FeatureProps {
  position: number;
  index: number;
  icon: IconType;
  image: string;
}

interface CollapsingCardsProps {
  title: RichTextField;
  description: RichTextField;
}

const Feature: React.FC<FeatureProps> = ({ position, index, icon: Icon, image }) => {
  const translateX = position >= index ? index * 100 : index * 100 - 100 * (index - position);

  return (
    <motion.div
      animate={{
        x: `${-translateX}%`,
        rotate: (() => {
          const rotations = [0, -2, 3, 0, -5];
          return rotations[index % rotations.length];
        })(),
      }}
      transition={{
        ease: 'easeInOut',
        duration: 0.35,
      }}
      className="collapse-card sm:aspect[3.5/2] relative flex aspect-[3/2] w-[calc(75vw-2rem)] max-w-[560px] shrink-0 overflow-hidden rounded-md border border-[#eee] bg-[#f7f7f7] bg-[length:100%] bg-no-repeat shadow-lg sm:w-[calc(35vw-2rem)] md:w-[calc(40vw-2rem)] xl:w-[calc(22vw-2rem)]"
      style={{
        backgroundImage: `url(${image})`,
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute left-4 top-4 hidden h-12 w-12 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm">
        <Icon className="text-2xl text-red-600" />
      </div>
    </motion.div>
  );
};

const features: Feature[] = [
  {
    icon: FiAward,
    image: '/2025_award.jpg?height=320&width=560',
  },
  {
    icon: FiAward,
    image: '/2024_award.jpg?height=320&width=560',
  },
  {
    icon: FiAward,
    image: '/erp_award.jpg?height=320&width=560',
  },
  {
    icon: FiAward,
    image: '/loyalty_21.jpg?height=320&width=560',
  },
  {
    icon: FiAward,
    image: '/4star_20.jpg?height=320&width=560',
  },
  {
    icon: FiAward,
    image: '/3star_21.jpg?height=320&width=560',
  },
];

const CollapsingCards: React.FC<CollapsingCardsProps> = ({ title, description }) => {
  const [position, setPosition] = useState<number>(0);
  const constraintsRef = useRef(null);
  const cardWidth = useRef(0);
  const controls = useAnimation();

  useEffect(() => {
    const updateCardWidth = () => {
      if (constraintsRef.current) {
        const firstCard = (constraintsRef.current as HTMLElement).children[0] as HTMLElement;
        if (firstCard) {
          cardWidth.current = firstCard.offsetWidth;
        }
      }
    };

    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  const shiftLeft = () => {
    if (position > 0) {
      setPosition((pv) => pv - 1);
    }
  };

  const shiftRight = () => {
    if (position < features.length - 1) {
      setPosition((pv) => pv + 1);
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = cardWidth.current / 4;
    if (info.offset.x > threshold && position > 0) {
      shiftLeft();
    } else if (info.offset.x < -threshold && position < features.length - 1) {
      shiftRight();
    } else {
      controls.start({ x: 0 });
    }
  };

  return (
    <section className="overflow-hidden bg-white px-4 py-10 sm:py-16 sm:pt-24">
      <div className="container mx-auto">
        <div className="fade-up mb-12 flex justify-between gap-4">
          <div className="relative text-center sm:text-left">
            <h2 className="mt-6 text-center text-3xl font-normal leading-tight text-black sm:mt-0 sm:text-left md:text-4xl">
              {asText(title)}
            </h2>
            <p className="mt-4 max-w-md text-base text-neutral-400">{asText(description)}</p>
          </div>
          <div className="relative hidden gap-2 sm:flex">
            <button
              className="h-fit rounded-full bg-blue-400 p-4 text-2xl text-white transition-colors hover:bg-blue"
              onClick={shiftLeft}
              aria-label="Previous feature"
            >
              <FiChevronLeft />
            </button>
            <button
              className="h-fit rounded-full bg-blue-400 p-4 text-2xl text-white transition-colors hover:bg-blue"
              onClick={shiftRight}
              aria-label="Next feature"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
        <div className="fade-up relative h-[calc((100vw-2rem)*2/3)] max-h-[320px] sm:h-[calc((46vw-2rem)*2/3.5)] lg:h-[calc((50vw-2rem)*2/3.5)] xl:h-[calc((30vw-2rem)*2/3.5)]">
          <motion.div
            ref={constraintsRef}
            className="absolute left-0 flex touch-pan-y gap-6 sm:gap-10"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            animate={controls}
          >
            {features.map((feat, index) => (
              <Feature {...feat} key={index} position={position} index={index} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CollapsingCards;
